import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook to manage display mode (totem or mobile) based on URL parameter
 *
 * Usage:
 * - ?view=totem → Force totem layout
 * - ?view=mobile → Force mobile layout
 * - (no param) → Auto-detect based on viewport (1080px breakpoint)
 *
 * This hook:
 * 1. Reads the ?view parameter from URL
 * 2. Sets data-mode="totem" attribute on <html> element
 * 3. CSS variables & Tailwind mode-totem: variant respond to this attribute
 * 4. Components can use isTotemMode for conditional rendering
 */
export function useViewMode() {
  const [searchParams] = useSearchParams();
  const [isTotemMode, setIsTotemMode] = useState(() => {
    // Default: use natural viewport breakpoint
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 1080;
  });

  useEffect(() => {
    const viewMode = searchParams.get('view'); // ?view=totem|mobile
    const htmlEl = document.documentElement;
    const viewportMeta = document.querySelector('meta[name="viewport"]');

    if (viewMode === 'totem') {
      htmlEl.setAttribute('data-mode', 'totem');
      setIsTotemMode(true);
      viewportMeta?.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    } else if (viewMode === 'mobile') {
      htmlEl.removeAttribute('data-mode');
      setIsTotemMode(false);
      viewportMeta?.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      htmlEl.removeAttribute('data-mode');
      setIsTotemMode(false);
      viewportMeta?.setAttribute('content', 'width=device-width, initial-scale=1.0');
      // No param: use natural viewport breakpoint
      // htmlEl.removeAttribute('data-mode');
      // setIsTotemMode(window.innerWidth >= 1080);
    }
  }, [searchParams]);

  return { isTotemMode };
}
